/**
 * Dashboard (S11).
 *
 * Five tiles, calls by hour, two breakdowns and an activity feed — every figure
 * aggregated from data this console already owns, so nothing here waits on the
 * platform.
 *
 * ## Why a trend is four fields and not a percentage
 *
 * Computing "▲ 12% vs yesterday" honestly needs more than a number, so a trend
 * carries what it compared against, in words, and its change is nullable — for
 * the case where there is no comparable period at all. A console one day old
 * should say "no earlier data" rather than "up 100%".
 */
import { z } from 'zod';

import { CallReason, CallResolution } from './enums.js';

/** A count or an average, against the period before. */
export const kpiTrendSchema = z.object({
  /** Null for an average over nothing: no calls, so no average duration. */
  value: z.number().min(0).nullable(),
  previous: z.number().min(0).nullable(),
  /**
   * Percentage change, or null when it cannot be stated.
   *
   * Null covers two cases that both matter: no earlier figure exists, and the
   * earlier figure was zero — where every non-zero present would be an infinite
   * rise. "Up from none" is a sentence; "+∞%" is not.
   */
  changePct: z.number().nullable(),
  /** What `previous` was, in the words the tile should use: "yesterday", "the prior 7 days". */
  comparedTo: z.string(),
});
export type KpiTrend = z.infer<typeof kpiTrendSchema>;

/**
 * A rate, against the period before.
 *
 * A rate's change is in percentage points, not percent: 20% to 25% abandoned is
 * "+5 pts", where "+25%" would read as a quarter of all calls.
 */
export const rateTrendSchema = z.object({
  /** 0–100, one decimal. Null when there was nothing to take a rate of. */
  value: z.number().min(0).max(100).nullable(),
  previous: z.number().min(0).max(100).nullable(),
  changePoints: z.number().nullable(),
  comparedTo: z.string(),
  /** How many calls the rate is over, so 100% of one call reads as what it is. */
  of: z.number().int().min(0),
});
export type RateTrend = z.infer<typeof rateTrendSchema>;

/** One hour of today, in `APP_TZ`. */
export const hourRowSchema = z.object({
  hour: z.number().int().min(0).max(23),
  /** Calls that started in this hour today. Null for hours still to come. */
  today: z.number().int().min(0).nullable(),
  /** The average for this hour on the same weekday in earlier weeks. */
  typical: z.number().min(0).nullable(),
});
export type HourRow = z.infer<typeof hourRowSchema>;

export const callsByHourSchema = z.object({
  hours: z.array(hourRowSchema).length(24),
  /** Today's weekday, in English: what `typical` is the typical of. */
  weekday: z.string(),
  /**
   * How many earlier same weekdays the average is over, up to four. Zero on a
   * console younger than a week, when there is no typical line to draw.
   */
  typicalWeeks: z.number().int().min(0),
});
export type CallsByHour = z.infer<typeof callsByHourSchema>;

/** One bar of the reason breakdown. */
export const reasonBreakdownRowSchema = z.object({
  reason: CallReason.schema,
  count: z.number().int().min(0),
});
export type ReasonBreakdownRow = z.infer<typeof reasonBreakdownRowSchema>;

/** One bar of the resolution breakdown. */
export const resolutionBreakdownRowSchema = z.object({
  resolution: CallResolution.schema,
  count: z.number().int().min(0),
});
export type ResolutionBreakdownRow = z.infer<typeof resolutionBreakdownRowSchema>;

/**
 * What happened, for the activity feed.
 *
 * Deliberately structured rather than a rendered sentence. The server knows what
 * occurred; the wording belongs with the rest of the labels, where a change to a
 * phrase does not mean a change to an API.
 */
export const ACTIVITY_KINDS = [
  'CALL_COMPLETED',
  'CALL_ABANDONED',
  'CALL_FAILED',
  'ESCALATION',
  'AGENT_UPDATED',
] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];
export const activityKindSchema = z.enum(ACTIVITY_KINDS);

export const activityItemSchema = z.object({
  /** Composite — `call:<id>`, `interaction:<id>` — because rows come from three tables. */
  id: z.string(),
  kind: activityKindSchema,
  /**
   * Who or what it is about, emphasised: the caller's stated name, the line they
   * rang, or the agent's name.
   */
  subject: z.string(),
  /** The call's reason, or the fields that changed. Null when there is nothing to add. */
  detail: z.string().nullable(),
  linkType: z.enum(['INTERACTION', 'AGENT', 'NONE']),
  linkId: z.string().nullable(),
  at: z.string().datetime(),
});
export type ActivityItem = z.infer<typeof activityItemSchema>;

/** How many feed rows the dashboard shows. */
export const ACTIVITY_LIMIT = 12;

export const dashboardSchema = z.object({
  /** Calls today against yesterday. */
  callsToday: kpiTrendSchema,
  /** In progress right now. */
  callsInFlight: z.number().int().min(0),
  /** Analysed calls resolved, over the last 7 days against the 7 before. */
  resolutionRate: rateTrendSchema,
  /** Average length of answered calls, in seconds, over the same windows. */
  averageDuration: kpiTrendSchema,
  /** Calls where the caller never spoke, over the same windows. */
  abandonedRate: rateTrendSchema,
  callsByHour: callsByHourSchema,
  /**
   * The first day of the 7-day window, as YYYY-MM-DD in `APP_TZ`, so a
   * breakdown bar can open the interactions list on exactly what it counted.
   */
  weekFrom: z.string(),
  /**
   * Every reason and resolution over the last 7 days, including those with no
   * calls. A breakdown that omitted empty rows would let "no complaints this
   * week" and "we are not counting complaints" look identical.
   */
  reasonBreakdown: z.array(reasonBreakdownRowSchema),
  resolutionBreakdown: z.array(resolutionBreakdownRowSchema),
  activity: z.array(activityItemSchema),
});
export type Dashboard = z.infer<typeof dashboardSchema>;
